import { Alert, Box, Button, Dialog, DialogContent, Stack, Tooltip, Typography } from "@mui/material";
import { useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import GaiaOptionsDropdown from "~/modules/app/components/GaiaOptionsDropdown";
import useApplyErrorToForm from "~/modules/app/hooks/useApplyErrorToForm";

import useCourseListRoute_GetAll from "../../routes/CourseListRoute/api/useCourseListRoute_GetAll";

type Props = {
    onSubmitted: (courseId: string) => void;
    excludeAssignedToUserId?: string;
    excludeAssignedToProtocolId?: string;
};

type FormValues = {
    courseId: string;
};

export default function CourseUserProtocolForm({ onSubmitted, excludeAssignedToUserId, excludeAssignedToProtocolId }: Props) {
    const { t } = useTranslation("course");
    const { data, error } = useCourseListRoute_GetAll({
        excludeAssignedToUserId,
        excludeAssignedToProtocolId,
    });
    const [open, setOpen] = useState(false);
    const handleOpen = () => {
        setOpen(true);
    };
    const handleClose = () => {
        setOpen(false);
    };

    const {
        handleSubmit,
        setError,
        control,
        formState: { errors, isLoading, isDirty },
        setValue,
    } = useForm<FormValues>({
        values: { courseId: "" },
    });

    const onSubmit: SubmitHandler<FormValues> = async submittedValues => {
        onSubmitted(submittedValues.courseId);
        setValue("courseId", "", { shouldDirty: true });
        handleClose();
    };

    useApplyErrorToForm(error, setError);

    const allCoursesAssigned = data?.length === 0;

    return (
        <Box>
            <Tooltip
                title={t("courseUserProtocol.btnTooltip")}
                disableHoverListener={!allCoursesAssigned}
                placement="bottom-start"
            >
                <span>
                    <Button onClick={handleOpen} disabled={allCoursesAssigned}>
                        {t("courseUserProtocol.type")}
                    </Button>
                </span>
            </Tooltip>

            <Dialog open={open} onClose={handleClose}>
                <DialogContent>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Typography variant="h6">{t("courseUserProtocol.type")}</Typography>
                        <Stack
                            gap={2}
                            sx={{
                                width: "400px",
                            }}
                        >
                            {errors.root && (
                                <Alert severity="error">
                                    <Typography>{errors.root.message}</Typography>
                                </Alert>
                            )}
                            <Controller
                                name="courseId"
                                control={control}
                                render={({ field }) => (
                                    <GaiaOptionsDropdown
                                        options={
                                            data?.map(item => ({
                                                value: item.id,
                                                label: item.name,
                                            })) ?? []
                                        }
                                        sx={{ mt: 2 }}
                                        label={t("courseUserProtocol.type")}
                                        error={!!errors.courseId}
                                        helperText={errors.courseId?.message}
                                        {...field}
                                    />
                                )}
                            />

                            <Button
                                sx={{ mt: 3 }}
                                type="submit"
                                color="primary"
                                variant="contained"
                                disabled={!isDirty}
                                loading={isLoading}
                            >
                                {t("courseUserProtocol.btnSubmit")}
                            </Button>
                        </Stack>
                    </form>
                </DialogContent>
            </Dialog>
        </Box>
    );
}
